import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { z } from "zod";
import { fetchFastApi } from "../api/fastapi";
import { FieldGroup, FieldGroupSchema } from "../models/audit/library";
import { toast } from "sonner";

interface FieldGroupsContextValue {
  fieldGroups: FieldGroup[];
  loading: boolean;
  refetch: () => Promise<void>;
}

const FieldGroupsContext = createContext<FieldGroupsContextValue | null>(null);

export function FieldGroupsProvider({ children }: { children: ReactNode }) {
  const [fieldGroups, setFieldGroups] = useState<FieldGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFieldGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchFastApi({
        path: "/controls/v2/field-groups",
        method: "GET",
      });

      const data = z.array(FieldGroupSchema).parse(response.fieldGroups || []);
      setFieldGroups(data);
    } catch (error) {
      console.error("Failed to fetch field groups:", error);
      toast.error("Failed to fetch libraries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFieldGroups();
  }, [fetchFieldGroups]);

  return (
    <FieldGroupsContext.Provider
      value={{
        fieldGroups,
        loading,
        refetch: fetchFieldGroups,
      }}
    >
      {children}
    </FieldGroupsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFieldGroupsContext() {
  const context = useContext(FieldGroupsContext);
  if (!context) {
    throw new Error(
      "useFieldGroupsContext must be used within a FieldGroupsProvider",
    );
  }
  return context;
}
